/**
 * @file AddAthleteDialog.tsx
 * @description Dialog for adding a new coached athlete profile
 * Validates connection to Intervals.icu before creating the profile
 */

import { useState } from 'react';
import { useStore } from '@/lib/store';
import { supabase } from '@/lib/supabase';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Loader2,
    User,
} from 'lucide-react';
import { InviteLinkDisplay } from './InviteLinkDisplay';
import { FEATURE_TRIATHLON } from '@/types';

interface AddAthleteDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: (profileId: string) => void;
}

// type ConnectionStatus = 'idle' | 'testing' | 'success' | 'error';


export function AddAthleteDialog({ open, onOpenChange, onSuccess }: AddAthleteDialogProps) {
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [sport, setSport] = useState<'Running' | 'Cycling' | 'Triathlon'>('Running');
    const [intervalsId, setIntervalsId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);


    const [inviteToken, setInviteToken] = useState('');

    const { addCoachedProfile, updateProfileAthlete, athlete, checkFeatureAccess } = useStore();
    const hasTriathlonAccess = checkFeatureAccess(FEATURE_TRIATHLON);

    const resetForm = () => {
        setEmail('');
        setDisplayName('');
        setSport('Running');
        setIntervalsId('');
        setIsSubmitting(false);
        setInviteToken('');
    };

    const handleClose = () => {
        resetForm();
        onOpenChange(false);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        if (!displayName.trim() || !email.trim()) {
            // setErrorMessage('Please fill in Name and Email.');
            setIsSubmitting(false);
            return;
        }

        try {
            // Call Edge Function to create silent user (Manual OR Connected)
            const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
            const coachId = currentUser?.id;
            // We no longer rely on coachApiKey for athlete connections

            if (userError || !coachId) {
                throw new Error("Could not identify your account. Please try logging out and back in.");
            }

            const session = await supabase.auth.getSession();
            const token = session.data.session?.access_token;

            if (!token) {
                throw new Error("No active session. Please log in again.");
            }

            const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`;
            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                },
                body: JSON.stringify({
                    email: email.trim(),
                    displayName: displayName.trim(),
                    sport,
                    coachId,
                    intervalsAthleteId: intervalsId.trim() || undefined,
                    // coachApiKey removed
                })
            });

            if (!response.ok) {
                const errorJson = await response.json().catch(() => ({}));
                throw new Error(errorJson.error || `Server returned ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (!data?.userId) throw new Error('No User ID returned from creation');

            // Set invite token to display UI
            if (data.inviteToken) {
                setInviteToken(data.inviteToken);
            }

            // Success! Add to local store using the REAL User UUID
            // Use placeholder ID for intervals ID since it will be linked later
            const storeIntervalsId = intervalsId.trim() || `pending-${data.userId.slice(0, 8)}`;

            const profileId = addCoachedProfile(storeIntervalsId, displayName.trim(), sport, {
                explicitId: data.userId,
                inviteToken: data.inviteToken,
                coachId: currentUser?.id || undefined,
                coachName: athlete.name || undefined
            });

            // Ensure email is saved to profile immediately
            updateProfileAthlete(profileId, { email: email.trim() });

            if (onSuccess) {
                onSuccess(profileId);
            }
            // Do NOT close dialog if we have a token to show
            if (!data.inviteToken) {
                handleClose();
            } else {
                setIsSubmitting(false); // Stop loading to show token UI
            }

        } catch (err: unknown) {
            console.error('Add failed:', err);
            let msg = 'Failed to add athlete';
            const e = err as { context?: { json?: { error?: string } }; message?: string };
            if (e.context?.json?.error) {
                msg = e.context.json.error;
            } else if (e.message) {
                msg = e.message;
            }
            console.error(msg); // Log failure message
            setIsSubmitting(false);
        }
    };

    const canSubmit = !isSubmitting && !inviteToken;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        {inviteToken ? 'Invitation Created' : 'Invite Athlete'}
                    </DialogTitle>
                    <DialogDescription>
                        {inviteToken
                            ? "Share this link with your athlete. Note: They will NOT receive an automatic email from us."
                            : "Create an invite URL for your athlete. You will need to send this to them manually."
                        }
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto px-1">

                    {inviteToken ? (
                        <InviteLinkDisplay
                            inviteToken={inviteToken}
                            displayName={displayName}
                            email={email}
                            onClose={handleClose}
                        />
                    ) : (
                        <>
                            {/* Email Field */}
                            <div className="space-y-2">
                                <Label htmlFor="email">Email Address <span className="text-destructive">*</span></Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="athlete@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>

                            {/* Display Name */}
                            <div className="space-y-2">
                                <Label htmlFor="displayName">Display Name <span className="text-destructive">*</span></Label>
                                <Input
                                    id="displayName"
                                    placeholder="Athlete's name"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                />
                            </div>

                            {/* Sport */}
                            <div className="space-y-2">
                                <Label htmlFor="sport">Primary Sport</Label>
                                <Select value={sport} onValueChange={(v) => setSport(v as 'Running' | 'Cycling' | 'Triathlon')}>
                                    <SelectTrigger id="sport">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Running">Running</SelectItem>
                                        <SelectItem value="Cycling">Cycling</SelectItem>
                                        {hasTriathlonAccess && (
                                            <SelectItem value="Triathlon">Triathlon</SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Intervals Athlete ID */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="intervalsAthleteId">Intervals.icu Athlete ID</Label>
                                    <span className="text-[10px] text-muted-foreground italic">Optional</span>
                                </div>
                                <Input
                                    id="intervalsAthleteId"
                                    placeholder="e.g. i12345"
                                    value={intervalsId}
                                    onChange={(e) => setIntervalsId(e.target.value)}
                                />
                                <p className="text-[11px] text-muted-foreground leading-tight">
                                    Required for you to push workouts to their calendar via your coach connection.
                                </p>
                            </div>
                        </>
                    )}
                </div>

                {!inviteToken && (
                    <DialogFooter className="flex-col gap-2 sm:flex-row">
                        <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                        >
                            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Generate Invite Link
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog >
    );
}

export default AddAthleteDialog;
